import React, { useEffect } from "react";
import {
	Modal,
	Button,
	Row,
	Col,
	Container,
	Form,
	Spinner,
} from "react-bootstrap";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import { getAllBanksAsync } from "../../../../slices/settings/settingsSlice";
import { createPettyRequestAsync } from "../../../../slices/requests/requestsSlice";
import { RootState } from "../../../../store/store";
import { Select, FormControl, InputLabel, MenuItem } from "@mui/material";
import { toast } from "react-toastify";
import CurrencyInput from "../../../../components/form/currency-input";
import {
	handleAccountNameChange,
	handleAccountNumberChange,
} from "util/inputUtils";

interface CreateUtilityRequestValues {
	company_id: string;
	title: string;
	receiver_name: string;
	bank_id: string;
	account_number: string;
	amount: string;
	projectId: string;
}
interface CreateUtilityRequestProps {
	closeModal: () => void;
	show: boolean;
	onCreateSuccess: () => void;
	projectId: string | undefined | null;
}

const CreateUtilityRequest: React.FC<CreateUtilityRequestProps> = ({
	closeModal,
	show,
	onCreateSuccess,
	projectId,
}) => {
	const dispatch = useAppDispatch();
	const banks = useAppSelector((state) => state.settings.banks);
	const companyId = useAppSelector(
		(state: RootState) => state.auth.user?.company_id || "",
	);
	useEffect(() => {
		dispatch(getAllBanksAsync());
	}, [dispatch]);

	return (
		<Container fluid className="">
			<Modal
				centered
				size="lg"
				show={show}
				onHide={closeModal}
				style={{ maxHeight: "100vh", overflowY: "auto" }}
			>
				<div className="px-5">
					<Formik
						initialValues={{
							company_id: companyId || "",
							title: "",
							receiver_name: "",
							bank_id: "",
							account_number: "",
							amount: "",
							projectId: projectId || "",
						}}
						validationSchema={Yup.object().shape({
							company_id: Yup.string().required("Required"),
							title: Yup.string().required("Required"),
							receiver_name: Yup.string().required("Required"),
							bank_id: Yup.string().required("Required"),
							account_number: Yup.string().required("10 digits Required").min(10).max(10),
							amount: Yup.string().required("Required"),
							projectId: Yup.string().required("Required"),
						})}
						onSubmit={(
							values: CreateUtilityRequestValues,
							{ setSubmitting },
						) => {
							console.log(values);
							const plainAmount = parseFloat(
								values.amount.replace(/[^0-9.]/g, ""),
							);
							const roundedAmount = Math.round(plainAmount);
							const addSpacesToMessage = (message: any) => {
								return message.replace(/([a-z])([A-Z])/g, '$1 $2');
							};
							dispatch(
								createPettyRequestAsync({
									...values,
									amount: roundedAmount.toString(),
									company_id: companyId || "",
									projectId: projectId || "",
								}),
							)
								.unwrap()
								.then((response) => {
									toast.success("Petty cash request created successfully");
									onCreateSuccess();
									closeModal();
								})
								.catch((error) => {
									const errorMessage = error.message ? addSpacesToMessage(error.message) : "An unexpected error occurred";
									toast.error(errorMessage);
								})
								.finally(() => {
									setSubmitting(false);
								});
						}}
					>
						{({
							errors,
							touched,
							handleSubmit,
							isSubmitting,
							values,
							handleChange,
							handleBlur,
						}) => (
							<Form onSubmit={handleSubmit}>
								<Modal.Header closeButton className="text-start mt-3">
									<h4 className="confirm-header">Create petty cash request</h4>
								</Modal.Header>
								<Modal.Body>
									<Row>
										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Title <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="title"
												className={`fluent-input  ${touched.title && errors.title ? "is-invalid" : ""
													}`}
												required
											/>
										</Col>
									</Row>
									<Row>
										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Receiver name <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="receiver_name"
												className={`fluent-input  ${touched.receiver_name && errors.receiver_name
													? "is-invalid"
													: ""
													}`}
												required
												onInput={handleAccountNameChange}
											/>
										</Col>

										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Bank <span className="text-danger">*</span>
												</p>
											</label>
											<FormControl fullWidth>
												<InputLabel>Select bank </InputLabel>
												<Select
													label="Select bank "
													name="bank_id"
													value={values.bank_id}
													onChange={handleChange}
												>
													{Array.isArray(banks) && banks.length > 0 ? (
														banks.map((bank) => (
															<MenuItem key={bank.id} value={bank.id}>
																{bank.bank_name}
															</MenuItem>
														))
													) : (
														<p> no banks </p>
													)}
												</Select>
											</FormControl>
											<div className="invalid-feedback d-block">
												{touched.bank_id && errors.bank_id}
											</div>
										</Col>
									</Row>
									<Row>
										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Account no <span className="text-danger">*</span>
												</p>
											</label>
											<Field
												type="text"
												name="account_number"
												className={`fluent-input  ${touched.account_number && errors.account_number
													? "is-invalid"
													: ""
													}`}
												required
												onInput={handleAccountNumberChange}
											/>
										</Col>

										<Col className="mt-2">
											<label className="mt-2">
												<p>
													Amount <span className="text-danger">*</span>
												</p>
											</label>
											<CurrencyInput
												name="amount"
												placeholder="Enter amount"
												className={`fluent-input  ${touched.amount && errors.amount ? "is-invalid" : ""
													}`}
												value={values.amount}
												onChange={handleChange}
												onBlur={handleBlur}
											/>
										</Col>
									</Row>

									<Modal.Footer className="mt-5">
										<Row>
											<Col md={6}>
												<div className=" mt-1">
													<Button onClick={closeModal} className="cancel-btn">
														<span className="text-center">Cancel </span>{" "}
													</Button>
												</div>
											</Col>
											<Col md={6}>
												<div className=" mt-1">
													<Button
														type="submit"
														className=" border-0 create-btn bg-color-1"
													>
														{isSubmitting ? (
															<Spinner animation="border" size="sm" />
														) : (
															<span className="text-center">
																Create request
															</span>
														)}
													</Button>
												</div>
											</Col>
										</Row>
									</Modal.Footer>
								</Modal.Body>
							</Form>
						)}
					</Formik>
				</div>
			</Modal>
		</Container>
	);
};

export default CreateUtilityRequest;
